import { useState } from "react";
import { useForm } from "react-hook-form";
import { makeStyles } from "tss-react/mui";
import {
  DocumentPreview,
  MiDetailPageHeader,
  MiInternalDrawer,
} from "@miplatform/components";
import {
  DATE_TIME_FORMATS,
  ENTITY_NAMES,
  HTTP_STATUSES,
} from "@miplatform/constants";
import { useRouter } from "@miplatform/hooks";
import { IDocumentListItemResponse } from "@miplatform/types";
import { toFormattedDate } from "@miplatform/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { Footer } from "@mibuildweb/components";
import { useBidRequests } from "@mibuildweb/hooks";
import { Box } from "@mui/material";
import { OPTION } from "./constants/constants";
import { schema } from "./schema/schema";
import { BidsSection } from "./BidSection/BidsSection";
import {
  CreateBidRequestApiPayload,
  CreateBidRequestFormData,
  bidCreateStatuses,
} from "./CreateBidRequest.type";
import { RequestDetailsSection } from "./RequestDetailsSection/RequestDetailsSection";

export function CreateBidRequest() {
  const [selectedOption, setSelectedOption] = useState("");
  const [isCreatingContact, setIsCreatingContact] = useState(false);
  const [bidStatuses, setBidStatuses] = useState<bidCreateStatuses>({});
  const [openDocumentViewer, setOpenDocumentViewer] = useState(false);
  const [selectedDoc, setSelectedDoc] =
    useState<IDocumentListItemResponse | null>(null);

  const form = useForm<CreateBidRequestFormData>({
    mode: "onChange",
    defaultValues: {
      bids: [],
      requestDate: toFormattedDate(
        new Date(),
        DATE_TIME_FORMATS.ISO_8601_EXTENDED_FORMAT
      ),
    },
    resolver: zodResolver(schema),
  });

  const {
    formState: { isValid },
    watch,
    getValues,
  } = form;

  const { bids = [] } = watch();
  const { navigate } = useRouter();
  const { classes, theme } = useStyles();
  const { isLoading, createBidRequest } = useBidRequests();

  const handleViewFile = (doc?: IDocumentListItemResponse | null) => {
    if (!doc) return;
    setSelectedDoc(doc);
    setOpenDocumentViewer(true);
  };

  const handleCloseDocumentViewer = () => {
    setSelectedDoc(null);
    setOpenDocumentViewer(false);
  };

  const handleCreateBids = async () => {
    const data = getValues();

    const payload: CreateBidRequestApiPayload = {
      name: data.name,
      builderId: data.builderId,
      requesterId: data.requesterId,
      dueDate: data.dueDate,
      requestDate: data.requestDate,
      plans: bids.map((b) => ({
        id: b.bidId,
        name: b.name,
        builderId: data.builderId,
        communityId: data.communityId,
        seriesId: data.seriesId,
        seriesGroupId: data.seriesGroupId,
        assigneeId: data.assigneeId,
        planStatus: "Assignment",
      })),
    };

    const response = await createBidRequest(payload);

    if (response.status === HTTP_STATUSES.OK) {
      navigate("/estimating/bids/list/new");
    }
  };

  return (
    <>
      <MiDetailPageHeader
        title={`Create ${ENTITY_NAMES.BID_REQUEST}`}
        style={{ backgroundColor: theme.palette.common.white }}
        loading={isLoading}
      />
      <Box className={classes.root}>
        <Box className={classes.contentBox}>
          <RequestDetailsSection
            form={form}
            selectedOption={selectedOption}
            setSelectedOption={setSelectedOption}
            isCreatingContact={isCreatingContact}
            setIsCreatingContact={setIsCreatingContact}
          />
          <BidsSection
            form={form}
            open={selectedOption === OPTION.NEW_BID}
            bidStatuses={bidStatuses}
            setBidStatuses={setBidStatuses}
            handleViewFile={handleViewFile}
          />
        </Box>
        <MiInternalDrawer
          title="Document Preview"
          open={openDocumentViewer}
          onClose={handleCloseDocumentViewer}
          width={400}
        >
          <DocumentPreview selectedDocument={selectedDoc} />
        </MiInternalDrawer>
      </Box>
      <Footer
        show
        saveTitle="Create"
        handleSaveClick={handleCreateBids}
        handleCancelClick={() => navigate(-1)}
        disableSave={
          isLoading || !isValid || bids.length === 0 || isCreatingContact
        }
      />
    </>
  );
}

const useStyles = makeStyles()((theme) => ({
  root: {
    display: "flex",
    flex: 1,
  },
  contentBox: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
    flexDirection: "column",
  },
}));
